import { useNavigate } from '@tanstack/react-router';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@sara/ui/components/command';
import { useDialog } from '@sara/ui/hooks/use-dialog';
import {
  AudioLines,
  CirclePlus,
  Globe,
  KeyRound,
  LayoutTemplate,
  Users,
  Webhook,
} from 'lucide-react';
import { useActiveOrganization } from '../../contexts/auth';
import { CreateInterviewDialog } from '../../contexts/interviews';
import { useCommandPalette } from './CommandPaletteProvider';
import { candidatesHomeSearch, interviewsHomeSearch } from '../../router/tree';

export function CommandPalette() {
  const { open, setOpen } = useCommandPalette();
  const { organization } = useActiveOrganization();
  const createDialog = useDialog(CreateInterviewDialog);
  const navigate = useNavigate();

  function runCommand(fn: () => void | Promise<void>) {
    setOpen(false);
    void fn();
  }

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Navigation">
          {organization && (
            <>
              <CommandItem
                onSelect={() =>
                  runCommand(() =>
                    navigate({
                      to: '/orgs/$orgSlug/sessions',
                      params: { orgSlug: organization.slug },
                      search: interviewsHomeSearch,
                    })
                  )
                }
              >
                <AudioLines className="mr-2 h-4 w-4" />
                <span>Sessions</span>
              </CommandItem>
              <CommandItem
                onSelect={() =>
                  runCommand(() =>
                    navigate({
                      to: '/orgs/$orgSlug/candidates',
                      params: { orgSlug: organization.slug },
                      search: candidatesHomeSearch,
                    })
                  )
                }
              >
                <Users className="mr-2 h-4 w-4" />
                <span>Candidates</span>
              </CommandItem>
              <CommandItem
                onSelect={() =>
                  runCommand(() =>
                    navigate({
                      to: '/orgs/$orgSlug/templates',
                      params: { orgSlug: organization.slug },
                    })
                  )
                }
              >
                <LayoutTemplate className="mr-2 h-4 w-4" />
                <span>Templates</span>
              </CommandItem>
              <CommandItem
                onSelect={() =>
                  runCommand(() =>
                    navigate({
                      to: '/orgs/$orgSlug/settings/api-keys',
                      params: { orgSlug: organization.slug },
                    })
                  )
                }
              >
                <KeyRound className="mr-2 h-4 w-4" />
                <span>API keys</span>
              </CommandItem>
              <CommandItem
                onSelect={() =>
                  runCommand(() =>
                    navigate({
                      to: '/orgs/$orgSlug/settings/webhooks',
                      params: { orgSlug: organization.slug },
                    })
                  )
                }
              >
                <Webhook className="mr-2 h-4 w-4" />
                <span>Webhooks</span>
              </CommandItem>
              <CommandItem
                onSelect={() =>
                  runCommand(() =>
                    navigate({
                      to: '/orgs/$orgSlug/settings/domains',
                      params: { orgSlug: organization.slug },
                    })
                  )
                }
              >
                <Globe className="mr-2 h-4 w-4" />
                <span>Custom domains</span>
              </CommandItem>
            </>
          )}
        </CommandGroup>
        {organization && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Actions">
              <CommandItem
                onSelect={() => runCommand(() => createDialog.open({ orgId: organization.id }))}
              >
                <CirclePlus className="mr-2 h-4 w-4" />
                <span>New interview</span>
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
